import locationPinIcon from "assets/images/location-pin-small.svg";
import {useLocation, useNavigate} from "react-router-dom";
import {common as stringsCommon, confirmTransfer as strings} from "strings";

import Button from "components/Button";
import MobileNavigationHeader from "components/MobileNavigationHeader";

import {useTransferInMutation} from "liberty-core/api/react-query/transfer";

import {useAlert} from "lib/alert";
import {useAuthenticatedUser} from "lib/auth";
import {useCurrentPharmacy} from "lib/useCurrentPharmacy";

import styles from "./ConfirmTransferPage.module.css";

type ConfirmationProps = {
  label: string;
  value: string;
};

function ConfirmationOutput(props: ConfirmationProps) {
  return (
    <div className={styles.confirmationContainer}>
      <div className={styles.labelText}>{props.label}</div>
      <div className={styles.valueText}>{props.value}</div>
    </div>
  );
}

interface PharmacyDetailsProps {
  patientId: string | null;
  pharmacyName: string;
  pharmacyPhone: string;
  pharmacyCity: string;
  pharmacyState: string;
  scriptOrDrug: string[];
}

export default function ConfirmTransferPage() {
  const [pharmacy] = useCurrentPharmacy();
  const location = useLocation();
  const {
    patientId,
    pharmacyName,
    pharmacyPhone,
    pharmacyCity,
    pharmacyState,
    scriptOrDrug,
  } = location.state as PharmacyDetailsProps;
  const [authenticatedUser] = useAuthenticatedUser();
  const selectedPatient = authenticatedUser?.data.linkedPatients?.find(
    (p) => p.patientId === patientId,
  );

  const navigate = useNavigate();
  const alert = useAlert();
  const {mutateAsync: submitTransferRequest, isLoading: loading} =
    useTransferInMutation();
  return (
    <div className={styles.container}>
      <MobileNavigationHeader backTo="/profile/transfer-in" />
      <div className={styles.confirmText}>{strings.details}</div>
      <div className={styles.pharmacyInfo}>
        <img src={locationPinIcon} alt="" />
        <span className={styles.pharmacyLabel}>{pharmacy?.name}</span>
      </div>
      {selectedPatient ? (
        <ConfirmationOutput
          label={strings.patientName}
          value={`${selectedPatient.firstName} ${selectedPatient.lastName}`}
        />
      ) : null}
      <ConfirmationOutput label={strings.pharmName} value={pharmacyName} />
      <ConfirmationOutput label={strings.pharmPhone} value={pharmacyPhone} />
      <ConfirmationOutput label={strings.pharmCity} value={pharmacyCity} />
      <ConfirmationOutput label={strings.pharmState} value={pharmacyState} />
      {scriptOrDrug.map((rxNumber, i) => {
        return (
          <ConfirmationOutput
            label={strings.medName}
            value={rxNumber}
            key={i}
          />
        );
      })}
      <Button
        className={styles.submitButton}
        loading={loading}
        onClick={() => {
          submitTransferRequest({
            patientId,
            pharmacyName: pharmacyName,
            pharmacyPhone: pharmacyPhone,
            pharmacyCity: pharmacyCity,
            pharmacyState: pharmacyState,
            scriptOrDrug: scriptOrDrug,
          })
            .then(() => {
              alert({
                title: stringsCommon.success,
                message: strings.successBody,
                callbackFn: () => {
                  localStorage.removeItem("pharmacy-name");
                  localStorage.removeItem("pharmacy-phone");
                  localStorage.removeItem("pharmacy-city");
                  localStorage.removeItem("pharmacy-state");
                  localStorage.removeItem("primary-rx");
                  localStorage.removeItem("rx-number-list");
                  navigate("/profile");
                },
              });
            })
            .catch((err) => {
              alert({
                title: stringsCommon.whoops,
                message:
                  err?.response?.data?.generic?.join("\n") ?? strings.errorBody,
              });
            });
        }}
      >
        {stringsCommon.submit}
      </Button>
    </div>
  );
}
